import type { BoardData } from '../model/schema.js';
import { itemName } from '../trace/ports.js';
import { CIRCUIT_LOAD_LIMIT } from '../validate/rules/circuit-overload.js';
import { voltageAccepted } from '../validate/rules/voltage-mismatch.js';
import { fieldColumns } from './columns.js';
import { round2 } from './helpers.js';

export interface PowerLoadRow {
  rowKey: string;
  itemId: string;
  item: string;
  sourceId: string;
  /** Source name, empty when the item is not assigned. */
  source: string;
  watts: number | '';
  /** Current draw at the source voltage, empty if unknown. */
  amps: number | '';
  plug: string;
  voltages: string;
  /** False if the source voltage is not accepted (and no transformer), true if fine, '' if unknown. */
  voltageOk: boolean | '';
}

export interface PowerSourceRow {
  rowKey: string;
  sourceId: string;
  name: string;
  voltage: number;
  frequency: number;
  plug: string;
  maxAmps: number | '';
  loadCount: number;
  loadWatts: number;
  loadAmps: number;
  /** Load as share of `maxAmps` (0..n), '' if no rating. */
  utilisation: number | '';
  /** Above the 80 % continuous load limit. */
  overloaded: boolean;
}

/**
 * Power plan: one row per item with power data, grouped by source name (unassigned last),
 * then item name.
 */
export function powerPlan(data: BoardData): PowerLoadRow[] {
  return Object.values(data.items)
    .filter((item) => item.power !== undefined)
    .map((item): PowerLoadRow => {
      const power = item.power!;
      const source = power.sourceId === undefined ? undefined : data.power[power.sourceId];
      const voltages = power.voltages ?? [];
      const known = source !== undefined && voltages.length > 0;
      return {
        rowKey: item.id,
        itemId: item.id,
        item: itemName(item),
        sourceId: source?.id ?? '',
        source: source?.name ?? '',
        watts: power.watts ?? '',
        amps:
          source !== undefined && power.watts !== undefined
            ? round2(power.watts / source.voltage)
            : '',
        plug: power.plug ?? '',
        voltages: voltages.join('/'),
        voltageOk: known
          ? source.transformer === true || voltageAccepted(source.voltage, voltages)
          : '',
      };
    })
    .sort(
      (a, b) =>
        Number(a.source === '') - Number(b.source === '') ||
        a.source.localeCompare(b.source) ||
        a.item.localeCompare(b.item) ||
        a.rowKey.localeCompare(b.rowKey),
    );
}

/** Totals per power source, ordered by name. */
export function powerSummary(data: BoardData): PowerSourceRow[] {
  return Object.values(data.power)
    .map((source): PowerSourceRow => {
      const loads = Object.values(data.items).filter((i) => i.power?.sourceId === source.id);
      const watts = loads.reduce((sum, i) => sum + (i.power?.watts ?? 0), 0);
      const amps = watts / source.voltage;
      const max = source.maxAmps;
      return {
        rowKey: source.id,
        sourceId: source.id,
        name: source.name,
        voltage: source.voltage,
        frequency: source.frequency,
        plug: source.plug,
        maxAmps: max ?? '',
        loadCount: loads.length,
        loadWatts: watts,
        loadAmps: round2(amps),
        utilisation: max === undefined ? '' : round2(amps / max),
        overloaded: max !== undefined && amps > CIRCUIT_LOAD_LIMIT * max,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name) || a.rowKey.localeCompare(b.rowKey));
}

export const POWER_PLAN_COLUMNS = fieldColumns<PowerLoadRow>('power', [
  'source',
  'item',
  'watts',
  'amps',
  'plug',
  'voltages',
  'voltageOk',
]);

export const POWER_SUMMARY_COLUMNS = fieldColumns<PowerSourceRow>('powerSource', [
  'name',
  'voltage',
  'frequency',
  'plug',
  'maxAmps',
  'loadCount',
  'loadWatts',
  'loadAmps',
  'utilisation',
  'overloaded',
]);
